import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { handleApiError } from "../lib/handleApiError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Search, ExternalLink, Phone, Filter, Users, Trash2 } from "lucide-react";
import PageTip from "../components/PageTip";
import { toast } from "sonner";
import CustomerForm, { emptyCustomer } from "../components/CustomerForm";
import CustomerAvatar from "../components/CustomerAvatar";
import { RiskBadge, StatusBadge, FlagPill } from "../components/CustomerBadges";
import { commitStagedDocs, emptyStagedDocs } from "../lib/customerDocs";
import HardDeleteDialog from "../components/HardDeleteDialog";

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCustomer());
  const [stagedDocs, setStagedDocs] = useState(emptyStagedDocs());
  const [saving, setSaving] = useState(false);
  const [blacklistState, setBlacklistState] = useState({ matches: [], mode: "warn" });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    const params = { ...(q ? { search: q } : {}), ...filters };
    const { data } = await api.get("/customers", { params });
    setRows(data);
  }, [q, filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/agents").then((r) => setAgents(r.data || [])).catch(() => {});
  }, []);

  const stageDoc = (entity, slot, file) => {
    setStagedDocs((prev) => ({
      ...prev,
      [entity]: { ...(prev[entity] || {}), [slot]: file },
    }));
  };

  const cleanPayload = (v) => {
    // Coerce numeric strings to numbers/null
    const numFields = ["years_at_current_address", "monthly_house_rent", "monthly_income", "years_in_business"];
    const out = { ...v };
    for (const k of numFields) {
      out[k] = out[k] === "" || out[k] == null ? null : Number(out[k]);
    }
    if (out.guarantor2 && !out.guarantor2.name) out.guarantor2 = null;
    if (!out.assigned_agent_id) out.assigned_agent_id = null;
    return out;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.guarantor1?.name?.trim()) {
      toast.error("Guarantor 1 ka naam zaroori hai");
      return;
    }
    // Blacklist soft-confirm in warn mode (block mode rejects via 409 anyway)
    if (blacklistState.matches.length > 0 && blacklistState.mode === "warn") {
      const ok = window.confirm(
        `Yeh customer blacklist mein match ho raha hai (${blacklistState.matches.length} entry). ` +
        `Phir bhi aage barhna hai?`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const { data: created } = await api.post("/customers", cleanPayload(form));
      // Commit staged photos / CNIC scans now that we have an ID.
      const docsResult = await commitStagedDocs(created.id, stagedDocs);
      if (docsResult?.failed) {
        toast.warning(`Customer added, ${docsResult.failed} document(s) failed to upload.`);
      } else {
        toast.success("Customer added");
      }
      setOpen(false);
      setForm(emptyCustomer());
      setStagedDocs(emptyStagedDocs());
      load();
    } catch (err) {
      handleApiError(err, "Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  const toggleFilter = (k, v) => setFilters((f) => (f[k] === v ? { ...f, [k]: undefined } : { ...f, [k]: v }));

  return (
    <div className="space-y-6" data-testid="customers-page">
      <PageTip
        id="customers"
        icon={Users}
        text="Start by adding your first customer with CNIC, photo, and guarantor details."
      />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Customers</h1>
          <p className="text-slate-500 mt-1">Complete onboarding with guarantor + risk assessment.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => {
          setOpen(o);
          if (!o) {
            setForm(emptyCustomer());
            setStagedDocs(emptyStagedDocs());
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="add-customer-dialog">
            <DialogHeader className="px-6 pt-6">
              <DialogTitle>New Customer Onboarding</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="px-6 pb-6 space-y-3">
              <CustomerForm
                value={form}
                onChange={setForm}
                agents={agents}
                stagedDocs={stagedDocs}
                onStageDoc={stageDoc}
                onBlacklistResult={setBlacklistState}
              />
              <DialogFooter className="sticky bottom-0 bg-white pt-3 -mx-6 px-6 border-t border-slate-200">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  type="submit"
                  disabled={saving || (blacklistState.matches.length > 0 && blacklistState.mode === "block")}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="cust-save-btn"
                >
                  {saving ? "Saving…" :
                    (blacklistState.matches.length > 0 && blacklistState.mode === "block")
                      ? "Blocked — Blacklisted"
                      : "Save Customer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, mobile, alt mobile, CNIC, guarantor, account #…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="cust-search-input"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs uppercase font-bold text-slate-500 mr-1 flex items-center gap-1">
              <Filter className="w-3 h-3" /> Filters:
            </span>
            {[
              ["risk_level", "low", "Low risk", "low"],
              ["risk_level", "medium", "Med risk", "medium"],
              ["risk_level", "high", "High risk", "high"],
              ["verification_status", "pending", "Unverified", "unverified"],
              ["verification_status", "verified", "Verified", "verified"],
              ["blacklisted", true, "Blacklisted", "blacklisted"],
              ["no_guarantor", true, "No guarantor", "no-guarantor"],
            ].map(([k, v, label, slug]) => (
              <button
                key={`${k}-${v}`}
                type="button"
                onClick={() => toggleFilter(k, v)}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition ${
                  filters[k] === v
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`cust-filter-${slug}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="text-sm text-slate-500 md:ml-auto">{rows.length} customer(s)</div>
        </div>
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">CNIC</th>
                <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Guarantor</th>
                <th className="text-left px-4 py-3 font-semibold">Risk</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid="cust-empty">
                  {q ? "No matches." : "No customers yet. Click 'Add Customer'."}
                </td></tr>
              )}
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cust-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-3">
                      <CustomerAvatar customer={c} size="md" testId={`cust-avatar-${c.id}`} />
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {c.name}
                        {c.blacklisted && <FlagPill kind="blacklisted" />}
                        {c.flag_high_risk && !c.blacklisted && <FlagPill kind="flagged" />}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular"><span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-slate-400" />{c.mobile}</span></td>
                  <td className="px-4 py-3 hidden md:table-cell tabular text-slate-600">{c.cnic || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-600">
                    {c.guarantor1?.name ? (
                      <div>
                        <div className="font-medium text-slate-800">{c.guarantor1.name}</div>
                        <div className="text-xs text-slate-500 tabular">{c.guarantor1.mobile || ""}</div>
                      </div>
                    ) : <span className="text-amber-700 text-xs font-semibold">⚠ Missing</span>}
                  </td>
                  <td className="px-4 py-3"><RiskBadge level={c.risk_level} score={c.risk_score} /></td>
                  <td className="px-4 py-3 hidden md:table-cell"><StatusBadge status={c.verification_status || "pending"} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/customers/${c.id}`} className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 font-semibold text-sm" data-testid={`cust-view-${c.id}`}>
                        View <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setDeleteTarget(c); }}
                        className="text-red-500 hover:text-red-700"
                        title="Delete permanently"
                        data-testid={`cust-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="cust-mobile-list">
          {rows.length === 0 && (
            <div className="px-4 py-10 text-center text-slate-500 text-sm" data-testid="cust-empty-mobile">
              {q ? "No matches." : "No customers yet. Tap 'Add Customer'."}
            </div>
          )}
          {rows.map((c) => (
            <Link
              key={c.id}
              to={`/customers/${c.id}`}
              className="flex items-center gap-3 px-4 py-3 active:bg-slate-50"
              data-testid={`cust-card-${c.id}`}
            >
              <CustomerAvatar customer={c} size="md" testId={`cust-avatar-m-${c.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold truncate">{c.name}</span>
                  {c.blacklisted && <FlagPill kind="blacklisted" />}
                  {c.flag_high_risk && !c.blacklisted && <FlagPill kind="flagged" />}
                </div>
                <div className="text-xs text-slate-500 tabular flex items-center gap-1 mt-0.5">
                  <Phone className="w-3 h-3" />{c.mobile}
                </div>
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  <RiskBadge level={c.risk_level} score={c.risk_score} />
                  <StatusBadge status={c.verification_status || "pending"} />
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
            </Link>
          ))}
        </div>
      </div>

      <HardDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Customer"
        targetLabel={deleteTarget?.name}
        endpoint={deleteTarget ? `/customers/${deleteTarget.id}` : ""}
        previewEndpoint={deleteTarget ? `/customers/${deleteTarget.id}/deletion-preview` : ""}
        description="Customer ke saare loans (closed), payments, follow-ups aur uploaded files permanently delete ho jayenge. Active loan hai to delete block ho jayega."
        onDeleted={() => { setDeleteTarget(null); load(); }}
      />
    </div>
  );
}
